import React, { useState } from 'react';
import './BookingForm.css';
import { useTranslation } from 'react-i18next';

const BookingForm = ({ selectedRange, totalPrice, prixParNuitDetails }) => {
  const { t, i18n } = useTranslation();
  
  const FRAIS_MENAGE = 120; // Frais de ménage fixes
  const totalPriceWithCleaning = totalPrice + FRAIS_MENAGE;

  const getNightCount = () => {
    if (!selectedRange) {
      return 0;
    }

    const start = new Date(selectedRange[0]);
    const end = new Date(selectedRange[1]);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return Math.round((end - start) / (1000 * 60 * 60 * 24));
  };

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    tel: '',
    commentaire: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [responseOK, setResponseOK] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false); // État pour afficher/masquer les détails

  const languageCode = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0];

  const nightsFallbackByLang = {
    fr: { one: 'nuit', other: 'nuits' },
    it: { one: 'notte', other: 'notti' },
    en: { one: 'night', other: 'nights' },
    de: { one: 'Nacht', other: 'Nachte' },
    es: { one: 'noche', other: 'noches' },
    pt: { one: 'noite', other: 'noites' },
    ru: { one: 'noch', other: 'nochey' }
  };

  const householdFallbackByLang = {
    fr: 'Menage',
    it: 'Pulizie',
    en: 'Cleaning',
    de: 'Reinigung',
    es: 'Limpieza',
    pt: 'Limpeza',
    ru: 'Uborka'
  };

  const WEBHOOK_URL = 'https://n8n.atoneo.com/webhook/dc995df4-1637-4515-bb09-248557253654';

const sendContactLog = async (formData) => {
  try {
    await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'contact_update',
        ...formData,
        explication: `Coordonnées modifiées par l'utilisateur sur le formulaire : ${JSON.stringify(formData)}`
      })
    });
  } catch (err) {
    console.error('[sendContactLog] Erreur lors de l\'envoi au webhook :', err);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = () => {
    sendContactLog(formData);
  };

  const getDetailsText = () => {
  return `
    ${formData.commentaire ? `Commentaire : ${formData.commentaire}` : ''}
    Sous-total hébergement : ${totalPrice}€
    Frais de ménage : ${FRAIS_MENAGE}€
    Total : ${totalPriceWithCleaning}€
    Détail des prix :
    ${prixParNuitDetails.map(ligne => `- ${ligne}`).join('\n')}
    - Frais de ménage : ${FRAIS_MENAGE}€
    `.trim();
    };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { nom, prenom, email, tel } = formData;
    if (!selectedRange || !email || !tel) {
      alert(t('booking.alertIncomplete'));
      return;
    }

    setLoading(true); // ← start loading

    const payload = {
      Voyageur: `${prenom} ${nom}`,
      Tel: tel,
      Mail: email,
      Plateforme: 'site',
      Détails: getDetailsText(),
      Arrivée: selectedRange[0].toLocaleDateString(),
      Départ: selectedRange[1].toLocaleDateString(),
      État: 'Demande',
      Notes: ''
    };

    try {
      const response = await fetch('https://n8n.atoneo.com/webhook/129a8e45-c523-4168-a2be-5e50ef0d3b65', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.text();
      setResponseOK(result.trim().toLowerCase() === 'ok');
    } catch (error) {
      console.error('Erreur lors de l’envoi au webhook :', error);
      setResponseOK(false);
    }

    setLoading(false); // ← stop loading
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <p className="confirmation-message">
        ✅ {responseOK ? t('booking.confirmation') : t('booking.confirmationError')}
      </p>
    );
  }

  return (
    <form className="booking-form" onSubmit={handleSubmit}>
      <h3>{t('booking.title')}</h3>

      {selectedRange && (
        <div className="recap">
          <p>📅 {t('calendar.selected.start')} <strong>{selectedRange[0].toLocaleDateString()}</strong> {t('calendar.selected.end')} <strong>{selectedRange[1].toLocaleDateString()}</strong></p>
          
          {(totalPrice === 0 || totalPrice === undefined || totalPrice === null) ? (
            <div className="booking-prob">
              {t('booking.prob')}
            </div>
          ) : (
            (() => {
              const nightCount = getNightCount();
              const nightsFallback = nightCount > 1
                ? (nightsFallbackByLang[languageCode]?.other || 'nights')
                : (nightsFallbackByLang[languageCode]?.one || 'night');
              const householdFallback = householdFallbackByLang[languageCode] || 'Cleaning';

              return (
                <div>
                  <div>
                    🏠 ({nightCount} {t('booking.nights', { count: nightCount, defaultValue: nightsFallback })}) : <strong>{totalPrice}€</strong>
                    <a 
                      type="button"
                      className="info-button"
                      onClick={() => setShowDetails(!showDetails)}
                      style={{ marginLeft: '10px', background: '#6a9bb0', border: 'none', cursor: 'pointer', fontSize: '1em', borderRadius: 5, padding:"2px 5px", boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}
                    >
                      {showDetails ? '❕' : '❔'}
                    </a>
                  </div>
                  
                  {showDetails && (
                    <div>
                      {prixParNuitDetails.map((ligne, index) => (
                        <div className='detailNuit' key={index}>- {ligne}</div>
                      ))}
                    </div>
                  )}
                  
                  <p style={{marginTop:20}}>🧹 {t('booking.household', { defaultValue: householdFallback })} : {FRAIS_MENAGE}€</p>
                  <p style={{marginTop:20}}><strong>💰 Total : {totalPriceWithCleaning}€</strong></p>
                </div>
              );
            })()
          )}
        </div>
      )}

      <label>{t('booking.tel')}</label>
      <input
        type="tel"
        name="tel"
        value={formData.tel}
        onChange={handleChange}
        onBlur={handleBlur}
        required
      />



      <label>{t('booking.email')}</label>
      <input
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        onBlur={handleBlur}
        required
      />

      <label>{t('booking.firstname')}</label>
      <input
        type="text"
        name="prenom"
        value={formData.prenom}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <label>{t('booking.lastname')}</label>
      <input
        type="text"
        name="nom"
        value={formData.nom}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      

      <label>Commentaire</label>
      <textarea
        name="commentaire"
        value={formData.commentaire}
        onChange={handleChange}
        onBlur={handleBlur}
        rows="4"
      />

      

      <button type="submit" disabled={loading}>
        {loading ? (
          <span className="loader"></span>
        ) : (
          t('booking.submit')
        )}
      </button>
    </form>
  );
};

export default BookingForm;
